import React from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  SelectChangeEvent,
} from '@mui/material';
import { School as SchoolIcon } from '@mui/icons-material';
import { useAvailableRoleContextsWithCurrent, useSwitchRoleContext } from '../hooks/useRoleContext';
import { RoleContextDTO } from '../dtos';
import { getRoleDisplayName } from '@shared/utils/roleUtils';

interface RoleContextSelectProps {
  /**
   * Visual variant of the component
   * - sidebar: Styled for sidebar placement
   * - header: Styled for header placement
   */
  variant?: 'sidebar' | 'header';

  /**
   * Size of the component
   */
  size?: 'small' | 'medium';

  /**
   * Additional styling
   */
  sx?: object;
}

/**
 * RoleContextSelect Component
 *
 * Provides role context switching functionality with:
 * - Current role context display
 * - Dropdown for multiple roles
 * - Static display when only one role
 * - Loading states during switching
 * - Consistent MUI styling with sidebar theme
 */
export const RoleContextSelect: React.FC<RoleContextSelectProps> = ({
  variant = 'sidebar',
  size = 'small',
  sx = {},
}) => {
  const theme = useTheme();

  // Data hooks
  const {
    availableContexts: contexts,
    currentContext,
    isLoading,
    error,
  } = useAvailableRoleContextsWithCurrent();
  const { switchRoleContext } = useSwitchRoleContext();

  /**
   * Generate subline text based on role type and context
   * For Lecturers/Course Reps: Just show the role name (tenant is redundant)
   * For Institute Admins: Just show the role name (domain is already in title)
   * For University Admins: Just show the role name (tenant is already in title)
   * For System Admins: Just show the role name (title shows "Administrator")
   */
  const getSublineText = (context: RoleContextDTO): string => {
    const roleName = getRoleDisplayName(context.roleType, true);

    // All role types just show the role name in the subline
    // The entity context (subject/domain/tenant) is already in the title
    return roleName;
  };

  // Handler for role context change
  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    const contextId = event.target.value;
    if (contextId && contextId !== currentContext?.contextId) {
      switchRoleContext(contextId, contexts);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="caption" sx={{ ml: 1 }}>
          Loading roles...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="error">
          Unable to load roles
        </Typography>
      </Box>
    );
  }

  // No roles available
  if (contexts.length === 0) {
    return null;
  }

  // Single role - show as plain text
  if (contexts.length === 1) {
    return (
      <Box
        sx={{
          mx: variant === 'sidebar' ? 2 : 0,
          mb: variant === 'sidebar' ? 1 : 0,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          ...sx,
        }}
      >
        <SchoolIcon
          sx={{
            mr: 1,
            fontSize: size === 'small' ? '1rem' : '1.25rem',
            color: 'text.secondary',
            flexShrink: 0,
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: size === 'small' ? '0.875rem' : '1rem',
              color: 'text.primary',
              wordBreak: 'break-word',
              lineHeight: 1.3,
            }}
          >
            {currentContext?.displayName || 'No role selected'}
          </Typography>
          {currentContext && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                wordBreak: 'break-word',
                lineHeight: 1.2,
              }}
            >
              {getSublineText(currentContext)}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Multiple roles - show as dropdown
  return (
    <FormControl
      size={size}
      sx={{
        mx: variant === 'sidebar' ? 2 : 0,
        mb: variant === 'sidebar' ? 1 : 0,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        ...sx,
      }}
    >
      <Select
        value={currentContext?.contextId || ''}
        onChange={handleRoleChange}
        disabled={false} // No longer have switching state since it's instant + reload
        displayEmpty
        startAdornment={
          <SchoolIcon
            sx={{
              mr: 1,
              fontSize: size === 'small' ? '1rem' : '1.25rem',
              color: 'text.secondary',
            }}
          />
        }
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor:
              variant === 'sidebar' ? 'rgba(237, 221, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            fontSize: size === 'small' ? '0.875rem' : '1rem',
            '&:hover': {
              backgroundColor:
                variant === 'sidebar' ? 'rgba(237, 221, 255, 0.2)' : 'rgba(0, 0, 0, 0.08)',
            },
            '&.Mui-focused': {
              backgroundColor:
                variant === 'sidebar' ? 'rgba(237, 221, 255, 0.15)' : 'rgba(0, 0, 0, 0.06)',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border:
                variant === 'sidebar'
                  ? '1px solid rgba(237, 221, 255, 0.3)'
                  : '1px solid rgba(0, 0, 0, 0.12)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor:
                variant === 'sidebar' ? 'rgba(237, 221, 255, 0.5)' : 'rgba(0, 0, 0, 0.23)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: '2px',
            },
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300,
              '& .MuiMenuItem-root': {
                fontSize: size === 'small' ? '0.875rem' : '1rem',
              },
            },
          },
        }}
      >
        {contexts.map((context: RoleContextDTO) => (
          <MenuItem
            key={context.contextId}
            value={context.contextId}
            sx={{
              py: 1,
              '&.Mui-selected': {
                backgroundColor: 'rgba(237, 221, 255, 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(237, 221, 255, 0.25)',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: context.contextId === currentContext?.contextId ? 600 : 400,
                  color: 'text.primary',
                  wordBreak: 'break-word',
                  lineHeight: 1.3,
                }}
              >
                {context.displayName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  wordBreak: 'break-word',
                  lineHeight: 1.2,
                }}
              >
                {getSublineText(context)}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
